import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faPaperPlane,
  faTimes,
  faSpinner,
  faPlus,
  faTrash,
  faCamera,
  faUpload
} from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DocumentScanner from '../BillProcessing/components/DocumentScanner';
// Old SpinningSankalvaXLogo import - commented out
// import SpinningSankalvaXLogo from '../UI/SpinningSankalvaXLogo';
// New Gruve Logo imports
import GruveLogo from '../UI/GruveLogo';
import SpinningGruveLogo from '../UI/SpinningGruveLogo';

// Custom styles for react-select to match the app theme
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none',
    '&:hover': {
      borderColor: '#3b82f6'
    },
    backgroundColor: 'var(--select-bg, white)',
    borderRadius: '0.375rem'
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    backgroundColor: 'var(--select-bg, white)'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#3b82f6'
      : state.isFocused
        ? '#eff6ff'
        : 'transparent',
    color: state.isSelected ? 'white' : '#374151',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#3b82f6'
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--select-text, #374151)'
  }),
  input: (base) => ({
    ...base,
    color: 'var(--select-text, #374151)'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af'
  })
};

// Dark mode styles
const selectStylesDark = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#3b82f6' : '#4b5563',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(59, 130, 246, 0.3)' : 'none',
    '&:hover': {
      borderColor: '#3b82f6'
    },
    backgroundColor: '#374151',
    borderRadius: '0.375rem'
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    backgroundColor: '#374151'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#3b82f6'
      : state.isFocused
        ? '#4b5563'
        : 'transparent',
    color: state.isSelected ? 'white' : '#e5e7eb',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#3b82f6'
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: '#e5e7eb'
  }),
  input: (base) => ({
    ...base,
    color: '#e5e7eb'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af'
  })
};

const iconMapping = {
  'envelope': faEnvelope,
  'paper-plane': faPaperPlane,
  'times': faTimes,
  'spinner': faSpinner
};

const FormRenderer = ({ definition, agentId, onClose, token, onSubmit, loading: externalLoading, workflowContext, embedded = false }) => {
  // Check if this is bank workflow (workflowId 16) - handle both string and number
  const isBankWorkflow = parseInt(workflowContext?.workflowId) === 16;

  // Auto-detect embedded mode when rendered inside a workflow context
  const isEmbedded = embedded || !!workflowContext;

  console.log('📋 FormRenderer props:', {
    externalLoading,
    workflowId: workflowContext?.workflowId,
    isBankWorkflow,
    shouldShowOverlay: externalLoading && isBankWorkflow
  });

  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({});  // Store fetched options by field name
  const [optionsLoading, setOptionsLoading] = useState({});  // Track loading state per field
  const [eolWarnings, setEolWarnings] = useState({});  // EOL item warnings { netsuite_item_id: eol_info }
  const [vendorWarnings, setVendorWarnings] = useState({});  // Items without vendors { netsuite_item_id: item_info }
  const [showScanner, setShowScanner] = useState(false);  // Document scanner modal visibility
  const [scannerFieldName, setScannerFieldName] = useState(null);  // Which field the scanner is for

  // Detect dark mode
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Check for dark mode
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark') ||
                     document.body.classList.contains('dark') ||
                     window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDarkMode(isDark);
    };

    checkDarkMode();

    // Listen for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // Initialize form data with default values from field definitions
  useEffect(() => {
    const initialData = {};
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    definition.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.default) {
          // Handle special default values
          if (field.default === 'today' && field.type === 'date') {
            initialData[field.name] = today;
          } else {
            initialData[field.name] = field.default;
          }
        }
      });
    });

    // Only set if we have defaults and formData is empty
    if (Object.keys(initialData).length > 0) {
      setFormData(prev => {
        // Don't overwrite existing values
        const merged = { ...initialData };
        Object.keys(prev).forEach(key => {
          if (prev[key]) merged[key] = prev[key];
        });
        return merged;
      });
    }
  }, [definition]);

  // Bank workflow specific: Progress message cycling
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);

  const bankProcessingMessages = [
    '📄 Extracting transactions...',
    '🔍 Analyzing transaction data...',
    '📊 Categorizing transactions...',
    '🏢 Mapping customers and vendors...',
    '✨ Preparing results...'
  ];

  // Only for Bank Workflow (workflowId === 16): Cycle through processing messages
  useEffect(() => {
    if (externalLoading && isBankWorkflow) {
      const interval = setInterval(() => {
        setProcessingMessageIndex(prev => (prev + 1) % bankProcessingMessages.length);
      }, 3000); // Change message every 3 seconds

      return () => clearInterval(interval);
    } else {
      setProcessingMessageIndex(0); // Reset when not loading
    }
  }, [externalLoading, isBankWorkflow]);

  // Fetch dynamic options for fields with options_url
  useEffect(() => {
    const fetchDynamicOptions = async () => {
      // Collect all fields with options_url from all sections
      const fieldsToFetch = [];

      console.log('🔍 FormRenderer: Scanning sections for options_url fields');
      console.log('🔍 Total sections:', definition.sections?.length || 0);

      definition.sections?.forEach((section, sectionIndex) => {
        console.log(`🔍 Section ${sectionIndex}: type=${section.type}, name=${section.name}, repeatable=${section.repeatable}, fields=${section.fields?.length || 0}`);
        section.fields?.forEach(field => {
          console.log(`   Field: ${field.name}, type=${field.type}, options_url=${field.options_url || 'none'}, depends_on=${field.depends_on || 'none'}`);
          // Skip fields that depend on another field - they will be fetched when parent is selected
          if (field.options_url && !field.depends_on) {
            fieldsToFetch.push({
              name: field.name,
              url: field.options_url,
              labelField: field.options_label_field || 'name',
              valueField: field.options_value_field || 'id'
            });
          }
        });
      });

      console.log('🔍 Fields to fetch:', fieldsToFetch.map(f => f.name));

      if (fieldsToFetch.length === 0) return;

      // Get token from multiple sources
      const getToken = () => {
        if (token) return token;
        const stored = localStorage.getItem('token');
        if (stored) return stored;
        // Try sessionStorage as fallback
        const session = sessionStorage.getItem('token');
        if (session) return session;
        return null;
      };

      const authToken = getToken();

      if (!authToken) {
        console.warn('No auth token available for fetching dynamic options');
        return;
      }

      // Fetch options for each field
      for (const field of fieldsToFetch) {
        setOptionsLoading(prev => ({ ...prev, [field.name]: true }));

        try {
          const separator = field.url.includes('?') ? '&' : '?';
          const url = `${field.url}${separator}token=${authToken}`;

          console.log(`🔄 Fetching options for ${field.name} from ${field.url}`);

          const response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            console.error(`Failed to fetch ${field.name}: ${response.status}`);
            setDynamicOptions(prev => ({ ...prev, [field.name]: [] }));
            continue;
          }

          const data = await response.json();

          // Find the array in response - check common keys: items, subsidiaries, vendors, customers, etc.
          let dataArray = null;
          if (data.items && Array.isArray(data.items)) {
            dataArray = data.items;
          } else if (data.subsidiaries && Array.isArray(data.subsidiaries)) {
            dataArray = data.subsidiaries;
          } else if (data.vendors && Array.isArray(data.vendors)) {
            dataArray = data.vendors;
          } else if (data.customers && Array.isArray(data.customers)) {
            dataArray = data.customers;
          } else if (data.templates && Array.isArray(data.templates)) {
            dataArray = data.templates;
          } else if (Array.isArray(data)) {
            dataArray = data;
          } else {
            // Try to find first array property in response
            const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
            if (arrayKey) {
              dataArray = data[arrayKey];
            }
          }

          if (dataArray) {
            const options = dataArray.map(item => ({
              value: String(item[field.valueField] || item.id),
              label: item[field.labelField] || item.name || item[field.valueField],
              has_vendor: item.has_vendor  // Include vendor config status for RFQ workflow
            }));

            console.log(`✅ Loaded ${options.length} options for ${field.name}`);
            setDynamicOptions(prev => ({ ...prev, [field.name]: options }));
          } else {
            console.warn(`No items found for ${field.name}`, data);
            setDynamicOptions(prev => ({ ...prev, [field.name]: [] }));
          }
        } catch (error) {
          console.error(`Failed to fetch options for ${field.name}:`, error);
          setDynamicOptions(prev => ({ ...prev, [field.name]: [] }));
        } finally {
          setOptionsLoading(prev => ({ ...prev, [field.name]: false }));
        }
      }
    };

    fetchDynamicOptions();
  }, [definition, token]);

  // Helper function to refresh options for dependent fields
  const refreshDependentOptions = async (dependentFieldName, parentValue) => {
    // Find the dependent field definition
    let dependentField = null;
    definition.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.name === dependentFieldName) {
          dependentField = field;
        }
      });
    });

    if (!dependentField || !dependentField.options_url) {
      return;
    }

    console.log(`🔄 Refreshing options for ${dependentFieldName} with parent value: ${parentValue}`);
    setOptionsLoading(prev => ({ ...prev, [dependentFieldName]: true }));

    try {
      const getToken = () => {
        if (token) return token;
        const stored = localStorage.getItem('token');
        if (stored) return stored;
        const session = sessionStorage.getItem('token');
        if (session) return session;
        return null;
      };

      const authToken = getToken();
      if (!authToken) {
        console.warn('No auth token available');
        return;
      }

      // Build URL with parent field value as query param
      const baseUrl = dependentField.options_url;
      const separator = baseUrl.includes('?') ? '&' : '?';
      const parentParam = dependentField.depends_on ? `${dependentField.depends_on}=${parentValue}` : '';
      const url = `${baseUrl}${separator}${parentParam}&token=${authToken}`;

      console.log(`🔄 Fetching dependent options from: ${url}`);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`Failed to fetch ${dependentFieldName}: ${response.status}`);
        setDynamicOptions(prev => ({ ...prev, [dependentFieldName]: [] }));
        return;
      }

      const data = await response.json();

      // Find the array in response
      let dataArray = data.items || data.subsidiaries || data.vendors || data.customers || data.templates;
      if (!dataArray && Array.isArray(data)) {
        dataArray = data;
      } else if (!dataArray) {
        const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
        if (arrayKey) dataArray = data[arrayKey];
      }

      if (dataArray) {
        const labelField = dependentField.options_label_field || 'name';
        const valueField = dependentField.options_value_field || 'id';
        const options = dataArray.map(item => ({
          value: String(item[valueField] || item.id),
          label: item[labelField] || item.name || item[valueField]
        }));
        console.log(`✅ Loaded ${options.length} dependent options for ${dependentFieldName}`);
        setDynamicOptions(prev => ({ ...prev, [dependentFieldName]: options }));
      } else {
        setDynamicOptions(prev => ({ ...prev, [dependentFieldName]: [] }));
      }
    } catch (error) {
      console.error(`Failed to fetch dependent options for ${dependentFieldName}:`, error);
      setDynamicOptions(prev => ({ ...prev, [dependentFieldName]: [] }));
    } finally {
      setOptionsLoading(prev => ({ ...prev, [dependentFieldName]: false }));
    }
  };

  const handleInputChange = (name, value) => {
    console.log('📝 FormRenderer handleInputChange:', { name, value });
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      console.log('📊 Updated form data:', newData);
      return newData;
    });

    // Check if any fields depend on this field and refresh their options
    definition.sections?.forEach(section => {
      section.fields?.forEach(field => {
        if (field.depends_on === name && value) {
          // Clear the dependent field value when parent changes
          setFormData(prev => ({ ...prev, [field.name]: '', [`${field.name}_label`]: '' }));
          // Refresh the dependent field's options with new parent value
          refreshDependentOptions(field.name, value);
        }
      });
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    definition.sections?.forEach(section => {
      // Handle repeatable sections validation
      if (section.repeatable && section.name) {
        const items = formData[section.name] || [{}];
        items.forEach((item, rowIndex) => {
          section.fields?.forEach(field => {
            if (field.required) {
              const fieldValue = item[field.name];
              if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
                newErrors[`${section.name}_${rowIndex}_${field.name}`] = `${field.label} is required in row ${rowIndex + 1}`;
              }
            }
          });
        });
        return; // Skip regular field processing for repeatable sections
      }

      // Regular field validation
      section.fields?.forEach(field => {
        const fieldValue = formData[field.name];

        // Required field validation
        if (field.required) {
          if (field.type === 'file') {
            // For file fields, check if a file was selected
            if (!fieldValue || !(fieldValue instanceof File)) {
              newErrors[field.name] = `${field.label} is required`;
            }
          } else if (field.type === 'checkbox') {
            // For checkbox fields, check if checked
            if (!fieldValue) {
              newErrors[field.name] = `${field.label} is required`;
            }
          } else {
            // For text fields, check if not empty after trimming
            if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
              newErrors[field.name] = `${field.label} is required`;
            }
          }
        }

        // Email validation
        if (field.type === 'email' && fieldValue && typeof fieldValue === 'string') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(fieldValue)) {
            newErrors[field.name] = 'Please enter a valid email address';
          }
        }

        // Date validation with min_field/max_field (same as normal RFQ form)
        if (field.type === 'date' && fieldValue) {
          // Validate min_field constraint (e.g., end_date must be > start_date)
          // Uses <= to match normal RFQ: "End date must be greater than start date"
          if (field.min_field && formData[field.min_field]) {
            const currentDate = new Date(fieldValue);
            const minDate = new Date(formData[field.min_field]);
            if (currentDate <= minDate) {
              const minFieldLabel = definition.sections
                ?.flatMap(s => s.fields || [])
                .find(f => f.name === field.min_field)?.label || field.min_field;
              newErrors[field.name] = `${field.label} must be greater than ${minFieldLabel}`;
            }
          }
          // Validate max_field constraint
          if (field.max_field && formData[field.max_field]) {
            const currentDate = new Date(fieldValue);
            const maxDate = new Date(formData[field.max_field]);
            if (currentDate >= maxDate) {
              const maxFieldLabel = definition.sections
                ?.flatMap(s => s.fields || [])
                .find(f => f.name === field.max_field)?.label || field.max_field;
              newErrors[field.name] = `${field.label} must be less than ${maxFieldLabel}`;
            }
          }
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAction = async (action, actionConfig) => {
    console.log('🔄 FormRenderer handleAction called:', { action, actionConfig, onSubmit: !!onSubmit, formData });

    // Handle close actions
    if (action === 'close_form' || actionConfig?.type === 'close') {
      onClose();
      return;
    }

    // Handle workflow submit actions (for UI→AI workflows)
    if (action === 'submit' && onSubmit) {
      console.log('📝 Validating form...');
      if (!validateForm()) {
        console.log('❌ Form validation failed');
        return;
      }

      console.log('✅ Form valid, calling onSubmit with data:', formData);
      // Call the workflow submit handler
      onSubmit(formData);
      return;
    }

    // FALLBACK: Handle submit actions with backend_call even without onSubmit (for bill processing)
    if (action === 'submit' && actionConfig?.type === 'backend_call') {
      console.log('🔧 FALLBACK: Handling backend_call submit action directly');
      if (!validateForm()) {
        console.log('❌ Form validation failed');
        return;
      }

      setLoading(true);
      try {
        // Submit directly to workflow continue endpoint
        const response = await fetch('/api/v2/workflows/continue', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            form_data: formData,
            action: actionConfig.function
          })
        });

        if (response.ok) {
          alert(actionConfig.success_message || 'Form submitted successfully!');
          onClose();
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || actionConfig.error_message || 'Submission failed');
        }
      } catch (error) {
        console.error('❌ Fallback submission failed:', error);
        alert(error.message || 'Submission failed');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle any action with an endpoint (traditional forms)
    if (actionConfig?.endpoint) {
      if (!validateForm()) {
        return;
      }

      setLoading(true);
      try {
        const endpoint = actionConfig.endpoint?.replace('{agent_id}', agentId);
        const response = await fetch(endpoint, {
          method: actionConfig.method || 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          alert(actionConfig.success_message || 'Form submitted successfully!');
          onClose();
        } else {
          throw new Error(actionConfig.error_message || 'Submission failed');
        }
      } catch (error) {
        alert(error.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // EMERGENCY FIX: Handle submit + backend_call directly
    if (action === 'submit' && actionConfig?.type === 'backend_call') {
      console.log('🚨 EMERGENCY FIX: Processing submit + backend_call');
      if (!validateForm()) {
        return;
      }

      setLoading(true);

      // Create FormData for file upload
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });

      fetch('/api/v2/workflows/14/continue', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submitData
      }).then(response => {
        if (response.ok) {
          alert(actionConfig.success_message || 'Success!');
          onClose();
        } else {
          alert('Submission failed');
        }
      }).catch(error => {
        alert('Error: ' + error.message);
      }).finally(() => {
        setLoading(false);
      });
      return;
    }

    // Unknown action
    console.log('Unknown action:', action, 'Config:', actionConfig);
  };

  const renderField = (field) => {
    const commonProps = {
      id: field.name,
      name: field.name,
      className: `w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        errors[field.name] ? 'border-red-500' : ''
      }`,
      placeholder: field.placeholder,
      value: formData[field.name] || '',
      onChange: (e) => handleInputChange(field.name, e.target.value)
    };

    // DEBUG: Log field type before switch
    console.log('🔧 renderField called:', field.name, 'type:', field.type, 'workflowId:', workflowContext?.workflowId);

    switch (field.type) {
      case 'text':
      case 'email':
        return <input type={field.type} {...commonProps} />;

      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={field.rows || 3}
          />
        );

      // MULTISELECT type with Select All option - Budget Agent (28) ONLY
      case 'multiselect':
        console.log('📊 MULTISELECT field detected:', field.name, 'workflowId:', workflowContext?.workflowId, 'type:', typeof workflowContext?.workflowId);
        // Agent protection: Only process multiselect for Budget Agent (28)
        // Support both number and string workflowId
        const parsedWorkflowId = parseInt(workflowContext?.workflowId);
        if (parsedWorkflowId !== 28) {
          // For other agents, fall back to regular select behavior
          console.warn('⚠️ multiselect type used outside Budget Agent (28), workflowId:', parsedWorkflowId, 'falling back to select');
          // Fall through to select case handled below
        } else {
          const multiselectOptions = dynamicOptions[field.name] || field.options || [];
          const isLoadingMultiselect = optionsLoading[field.name];
          console.log('📊 MULTISELECT options for', field.name, ':', multiselectOptions.length, 'options, loading:', isLoadingMultiselect);

          // Convert to react-select format
          const multiReactOptions = multiselectOptions.map(opt => ({
            value: opt.value,
            label: opt.label
          }));

          // Get current selected values (array of IDs)
          const currentMultiValues = formData[field.name] || [];
          const selectedMultiValues = Array.isArray(currentMultiValues)
            ? multiReactOptions.filter(opt => currentMultiValues.includes(opt.value))
            : [];

          // Check if all options are selected
          const allSelected = multiReactOptions.length > 0 &&
            currentMultiValues.length === multiReactOptions.length;

          return (
            <div className="space-y-2">
              {/* Select All checkbox - only show if selectAll option is enabled */}
              {field.selectAll && multiReactOptions.length > 0 && (
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // Select all options
                        const allValues = multiReactOptions.map(opt => opt.value);
                        handleInputChange(field.name, allValues);
                        // Also store labels for display
                        const allLabels = multiReactOptions.map(opt => opt.label);
                        handleInputChange(`${field.name}_labels`, allLabels);
                      } else {
                        // Deselect all
                        handleInputChange(field.name, []);
                        handleInputChange(`${field.name}_labels`, []);
                      }
                    }}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="font-medium">
                    Select All ({multiReactOptions.length} subsidiaries)
                  </span>
                </label>
              )}

              <Select
                inputId={field.name}
                name={field.name}
                options={multiReactOptions}
                value={selectedMultiValues}
                onChange={(selected) => {
                  const values = selected ? selected.map(s => s.value) : [];
                  handleInputChange(field.name, values);
                  // Also store labels for display
                  const labels = selected ? selected.map(s => s.label) : [];
                  handleInputChange(`${field.name}_labels`, labels);
                }}
                isMulti
                isLoading={isLoadingMultiselect}
                isDisabled={isLoadingMultiselect}
                isClearable
                isSearchable
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder={isLoadingMultiselect ? 'Loading subsidiaries...' : (field.placeholder || 'Click to select subsidiaries...')}
                noOptionsMessage={() => isLoadingMultiselect ? 'Loading...' : 'No subsidiaries found'}
                loadingMessage={() => 'Loading subsidiaries...'}
                styles={{
                  ...(isDarkMode ? selectStylesDark : selectStyles),
                  control: (base, state) => ({
                    ...base,
                    minHeight: '42px',
                    borderColor: state.isFocused ? '#6366f1' : (isDarkMode ? '#4b5563' : '#d1d5db'),
                    boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.3)' : 'none',
                    backgroundColor: isDarkMode ? '#374151' : 'white',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#6366f1'
                    }
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: '#6366f1',
                    '&:hover': {
                      color: '#4f46e5'
                    }
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: isDarkMode ? '#4f46e5' : '#e0e7ff',
                    borderRadius: '0.375rem'
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: isDarkMode ? 'white' : '#4338ca',
                    fontWeight: 500
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: isDarkMode ? '#c7d2fe' : '#6366f1',
                    '&:hover': {
                      backgroundColor: isDarkMode ? '#4338ca' : '#c7d2fe',
                      color: isDarkMode ? 'white' : '#4338ca'
                    }
                  })
                }}
                classNamePrefix="react-select"
              />

              {/* Show count of selected items */}
              {currentMultiValues.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {currentMultiValues.length} of {multiReactOptions.length} selected
                </p>
              )}
            </div>
          );
        }
        // If not Budget Agent (28), fall through to regular select

      case 'select':
        // Use dynamic options if available, otherwise fall back to static options
        const selectOptions = dynamicOptions[field.name] || field.options || [];
        const isLoadingOptions = optionsLoading[field.name];

        // Use searchable select for fields with options_url or explicitly marked as searchable
        const useSearchableSelect = field.options_url || field.searchable;

        // ASYNC SEARCH: Only for fields explicitly marked with async_search: true
        // This is used by RFQ Workflow Agent for item search - does NOT affect other workflows
        if (field.async_search && field.options_url) {
          // Convert initial options to react-select format
          const initialOptions = selectOptions.map(opt => ({
            value: opt.value,
            label: opt.label
          }));

          const selectedValue = formData[field.name]
            ? initialOptions.find(opt => opt.value === formData[field.name]) || { value: formData[field.name], label: formData[`${field.name}_label`] || formData[field.name] }
            : null;

          // Async load function for searching
          const loadAsyncOptions = async (inputValue) => {
            if (!inputValue || inputValue.length < 2) {
              return initialOptions.slice(0, 50); // Return first 50 when no search
            }

            try {
              // Get token from prop first, then localStorage/sessionStorage
              const authToken = token || localStorage.getItem('token') || sessionStorage.getItem('token');
              if (!authToken) {
                console.warn('No auth token available for async search');
                return initialOptions;
              }
              const separator = field.options_url.includes('?') ? '&' : '?';
              const url = `${field.options_url}${separator}search=${encodeURIComponent(inputValue)}&token=${authToken}`;

              const response = await fetch(url, {
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json'
                }
              });

              if (!response.ok) return initialOptions;

              const data = await response.json();
              let dataArray = data.items || data.subsidiaries || data.vendors || data.customers || [];

              const labelField = field.options_label_field || 'name';
              const valueField = field.options_value_field || 'id';

              return dataArray.map(item => ({
                value: String(item[valueField]),
                label: item[labelField] || item.name || item.displayname || `Item ${item[valueField]}`
              }));
            } catch (error) {
              console.error('Async search error:', error);
              return initialOptions;
            }
          };

          return (
            <AsyncSelect
              inputId={field.name}
              name={field.name}
              defaultOptions={initialOptions.slice(0, 50)}
              loadOptions={loadAsyncOptions}
              value={selectedValue}
              onChange={(selected) => {
                const value = selected?.value || '';
                handleInputChange(field.name, value);
                if (selected?.label) {
                  handleInputChange(`${field.name}_label`, selected.label);
                }
              }}
              cacheOptions
              isClearable
              isSearchable
              placeholder={field.placeholder || 'Type to search...'}
              noOptionsMessage={({ inputValue }) => inputValue?.length < 2 ? 'Type 2+ characters to search' : 'No results found'}
              loadingMessage={() => 'Searching...'}
              styles={isDarkMode ? selectStylesDark : selectStyles}
              classNamePrefix="react-select"
            />
          );
        }

        if (useSearchableSelect) {
          // Convert options to react-select format
          const reactSelectOptions = selectOptions.map(opt => ({
            value: opt.value,
            label: opt.label
          }));

          const selectedValue = formData[field.name]
            ? reactSelectOptions.find(opt => opt.value === formData[field.name])
            : null;

          return (
            <Select
              inputId={field.name}
              name={field.name}
              options={reactSelectOptions}
              value={selectedValue}
              onChange={(selected) => {
                const value = selected?.value || '';
                handleInputChange(field.name, value);

                // Also capture the label for any select with options_url
                if (selected?.label) {
                  handleInputChange(`${field.name}_label`, selected.label);
                }

                // Handle data_mapping
                if (value && field.data_mapping && selectOptions) {
                  const selectedOption = selectOptions.find(opt => opt.value === value);
                  if (selectedOption) {
                    Object.entries(field.data_mapping).forEach(([targetField, sourceKey]) => {
                      if (selectedOption[sourceKey]) {
                        handleInputChange(targetField, selectedOption[sourceKey]);
                      }
                    });
                  }
                }
              }}
              isLoading={isLoadingOptions}
              isDisabled={isLoadingOptions}
              isClearable
              isSearchable
              placeholder={isLoadingOptions ? 'Loading...' : (field.placeholder || 'Search and select...')}
              noOptionsMessage={() => isLoadingOptions ? 'Loading...' : 'No options found'}
              styles={isDarkMode ? selectStylesDark : selectStyles}
              classNamePrefix="react-select"
            />
          );
        }

        // Standard HTML select for non-searchable fields
        return (
          <div className="relative">
            <select
              {...commonProps}
              value={formData[field.name] || ''}
              disabled={isLoadingOptions}
              onChange={(e) => {
                const selectedValue = e.target.value;
                handleInputChange(field.name, selectedValue);

                // For bank field, also capture the bank name (label)
                if (field.name === 'bank' && selectedValue && selectOptions) {
                  const selectedOption = selectOptions.find(opt => opt.value === selectedValue);
                  if (selectedOption && selectedOption.label) {
                    handleInputChange('bank_name', selectedOption.label);
                  }
                }

                // Also capture the label for any select with options_url (for customer_name, item_name)
                if (field.options_url && selectedValue && selectOptions) {
                  const selectedOption = selectOptions.find(opt => opt.value === selectedValue);
                  if (selectedOption && selectedOption.label) {
                    handleInputChange(`${field.name}_label`, selectedOption.label);
                  }
                }

                // ✅ SAFE: Only auto-populate if field has data_mapping config
                // This won't affect bill processing which doesn't use data_mapping
                if (selectedValue && field.data_mapping && selectOptions) {
                  const selectedOption = selectOptions.find(opt => opt.value === selectedValue);
                  if (selectedOption) {
                    // Auto-populate fields based on data_mapping configuration
                    Object.entries(field.data_mapping).forEach(([targetField, sourceKey]) => {
                      if (selectedOption[sourceKey]) {
                        handleInputChange(targetField, selectedOption[sourceKey]);
                      }
                    });
                  }
                }
              }}
            >
              <option value="">
                {isLoadingOptions ? 'Loading...' : (field.placeholder || 'Select an option')}
              </option>
              {selectOptions.map((option, index) => (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {isLoadingOptions && (
              <div className="absolute right-8 top-1/2 -translate-y-1/2">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
              </div>
            )}
          </div>
        );

      case 'date':
        // Support min_field for dynamic date validation (e.g., end_date >= start_date)
        const minDate = field.min_field ? formData[field.min_field] : field.min;
        const maxDate = field.max_field ? formData[field.max_field] : field.max;
        return (
          <input
            type="date"
            id={field.name}
            name={field.name}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors[field.name] ? 'border-red-500' : ''
            }`}
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            min={minDate}
            max={maxDate}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            id={field.name}
            name={field.name}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors[field.name] ? 'border-red-500' : ''
            }`}
            placeholder={field.placeholder}
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            min={field.min}
            max={field.max}
            step={field.step || 1}
          />
        );

      case 'checkbox':
        return (
          <input
            type="checkbox"
            id={field.name}
            name={field.name}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            checked={formData[field.name] || false}
            onChange={(e) => handleInputChange(field.name, e.target.checked)}
          />
        );

      case 'file':
        // Check if this is the Bill Processing agent's bill_file field - ONLY show scanner for this specific field
        // Note: The workflow YAML in database uses 'bill_file', frontend component uses 'bill_document'
        const isBillDocumentField = field.name === 'bill_file' || field.name === 'bill_document';

        return (
          <div className={`professional-upload-container ${field.className || ''}`}>
            {/* Upload and Scan Buttons Row - Only for Bill Processing agent */}
            {!formData[field.name] && isBillDocumentField && (
              <div className="flex gap-3 mb-3">
                {/* Upload Button */}
                <label
                  htmlFor={field.name}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3
                             bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-lg
                             cursor-pointer hover:from-indigo-600 hover:to-purple-700 transition-all shadow-md
                             ${(externalLoading || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FontAwesomeIcon icon={faUpload} />
                  Upload File
                </label>

                {/* Scan Button - Only for Bill Processing agent (bill_document field) */}
                <button
                  type="button"
                  onClick={() => {
                    setScannerFieldName(field.name);
                    setShowScanner(true);
                  }}
                  disabled={externalLoading || loading}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3
                             bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-lg
                             hover:from-green-600 hover:to-emerald-700 transition-all shadow-md
                             ${(externalLoading || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <FontAwesomeIcon icon={faCamera} />
                  Scan Document
                </button>
              </div>
            )}

            <div className="upload-area">
              <input
                type="file"
                id={field.name}
                name={field.name}
                className="upload-input"
                accept={field.accept || '*'}
                disabled={externalLoading || loading}
                onChange={(e) => {
                  const file = e.target.files[0];
                  console.log('📎 File selected:', file);
                  if (file) {
                    // Check file size if specified
                    if (field.max_size) {
                      const maxBytes = parseMaxSize(field.max_size);
                      if (file.size > maxBytes) {
                        alert(`File size must be less than ${field.max_size}`);
                        e.target.value = '';
                        return;
                      }
                    }
                    console.log('✅ File validated, updating form data');
                    handleInputChange(field.name, file);
                  }
                }}
              />
              <label htmlFor={field.name} className={`upload-label ${(externalLoading || loading) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                <div className="upload-icon">📄</div>
                <div className="upload-text">
                  {formData[field.name] ? (
                    <div className="file-selected-container">
                      <span className="file-selected">{formData[field.name].name}</span>
                      {!(externalLoading || loading) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Clear the file from form data
                            handleInputChange(field.name, null);
                            // Reset the file input
                            const fileInput = document.getElementById(field.name);
                            if (fileInput) fileInput.value = '';
                          }}
                          className="ml-2 text-red-500 hover:text-red-700 text-sm font-medium"
                          title="Remove file"
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <span className="upload-main">Click to upload or drag and drop</span>
                      <span className="upload-sub">{field.helpText || 'Select your file'}</span>
                    </>
                  )}
                </div>
              </label>
            </div>
          </div>
        );

      // Display type - read-only field for displaying data
      // Note: Budget Agent (28) uses custom components via CustomComponentRenderer
      case 'display':
        return <input type="text" {...commonProps} readOnly />;

      default:
        console.warn('⚠️ Unknown field type falling to default text:', field.name, 'type:', field.type);
        return <input type="text" {...commonProps} />;
    }
  };

  const renderContent = (content, index = 0) => {
    switch (content.type) {
      case 'text':
        const Tag = content.tag || 'p';
        return (
          <Tag
            key={`text-${index}`}
            className={content.css_class || ''}
            style={content.style ? parseStyle(content.style) : {}}
          >
            {content.content}
          </Tag>
        );

      case 'icon':
        return (
          <div
            key={`icon-${index}`}
            className={content.css_class || ''}
            style={content.style ? parseStyle(content.style) : {}}
          >
            <FontAwesomeIcon
              icon={iconMapping[content.icon] || faEnvelope}
            />
          </div>
        );

      case 'button':
        const actionConfig = definition.actions?.[content.action];
        return (
          <button
            key={`button-${index}`}
            className={content.css_class || 'btn'}
            style={content.style ? parseStyle(content.style) : {}}
            onClick={() => handleAction(content.action, actionConfig)}
            disabled={loading}
          >
            {loading && content.action === 'submit_form' ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            ) : content.icon ? (
              <FontAwesomeIcon
                icon={iconMapping[content.icon] || faEnvelope}
                className="mr-2"
              />
            ) : null}
            {content.text}
          </button>
        );

      default:
        return null;
    }
  };

  // Check if this is RFQ Workflow Agent (only check EOL for RFQ workflows)
  const isRFQWorkflow = () => {
    // Check URL path
    if (window.location.pathname.includes('rfq-workflow')) return true;
    // Check workflow context (workflowId is camelCase)
    if (parseInt(workflowContext?.workflowId) === 25) return true;
    if (workflowContext?.workflow_id === 25) return true;
    if (workflowContext?.workflow_name?.toLowerCase().includes('rfq')) return true;
    return false;
  };

  // Check if item is EOL (End of Life) - Only for RFQ Workflow
  const checkItemEOL = async (netsuiteItemId, itemLabel) => {
    console.log('[EOL CHECK] Called with:', { netsuiteItemId, itemLabel, isRFQ: isRFQWorkflow() });
    // Only check EOL for RFQ Workflow Agent
    if (!isRFQWorkflow()) {
      console.log('[EOL CHECK] Skipped - not RFQ workflow');
      return;
    }
    if (!netsuiteItemId) {
      console.log('[EOL CHECK] Skipped - no item ID');
      return;
    }

    try {
      const authToken = token || localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('[EOL CHECK] Fetching EOL status for:', netsuiteItemId);
      const response = await fetch('/api/rfq-workflow/check-eol-items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ netsuite_item_ids: [netsuiteItemId] })
      });

      if (!response.ok) {
        // Silently fail for non-RFQ agents or if endpoint doesn't exist
        return;
      }

      const data = await response.json();
      console.log('[EOL CHECK] API Response:', data);
      if (data.has_eol_items && data.eol_items?.length > 0) {
        const eolItem = data.eol_items[0];
        console.log('[EOL CHECK] Found EOL item! Setting warning for:', netsuiteItemId, eolItem);
        setEolWarnings(prev => ({
          ...prev,
          [netsuiteItemId]: {
            ...eolItem,
            itemLabel: itemLabel
          }
        }));
      } else {
        console.log('[EOL CHECK] No EOL found for:', netsuiteItemId);
        // Remove warning if item is not EOL
        setEolWarnings(prev => {
          const updated = { ...prev };
          delete updated[netsuiteItemId];
          return updated;
        });
      }
    } catch (error) {
      // Silently fail - don't break other agents
      console.debug('EOL check skipped:', error.message);
    }
  };

  // Handle repeatable section item changes
  const handleRepeatableItemChange = (sectionName, rowIndex, fieldName, value, itemLabel = null) => {
    console.log('[REPEATABLE CHANGE]', { sectionName, rowIndex, fieldName, value, itemLabel });
    setFormData(prev => {
      const items = prev[sectionName] || [{}];
      const updatedItems = [...items];
      updatedItems[rowIndex] = {
        ...updatedItems[rowIndex],
        [fieldName]: value
      };
      return { ...prev, [sectionName]: updatedItems };
    });

    // Check EOL for item selection fields (only for RFQ Workflow)
    if (value && (fieldName === 'netsuite_item_id' || fieldName === 'item_id')) {
      checkItemEOL(value, itemLabel);
    }
  };

  // Add new row to repeatable section
  const addRepeatableRow = (sectionName) => {
    setFormData(prev => {
      const items = prev[sectionName] || [{}];
      return { ...prev, [sectionName]: [...items, {}] };
    });
  };

  // Remove row from repeatable section
  const removeRepeatableRow = (sectionName, rowIndex) => {
    setFormData(prev => {
      const items = prev[sectionName] || [];
      if (items.length <= 1) return prev; // Keep at least one row
      const updatedItems = items.filter((_, idx) => idx !== rowIndex);
      return { ...prev, [sectionName]: updatedItems };
    });
  };

  // Render a field for repeatable section
  const renderRepeatableField = (field, sectionName, rowIndex, rowData) => {
    const fieldValue = rowData[field.name] || '';
    const fieldId = `${sectionName}_${rowIndex}_${field.name}`;

    const commonProps = {
      id: fieldId,
      name: fieldId,
      className: `w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white`,
      placeholder: field.placeholder || field.label,
      value: fieldValue,
      onChange: (e) => handleRepeatableItemChange(sectionName, rowIndex, field.name, e.target.value)
    };

    switch (field.type) {
      case 'select':
        // Use dynamic options if available for repeatable fields too
        const repeatableSelectOptions = dynamicOptions[field.name] || field.options || [];
        const isRepeatableLoading = optionsLoading[field.name];

        console.log(`🔧 Repeatable select field: ${field.name}, options count: ${repeatableSelectOptions.length}, loading: ${isRepeatableLoading}, async_search: ${field.async_search}`);

        // Use searchable select for fields with options_url or explicitly marked as searchable
        const useRepeatableSearchable = field.options_url || field.searchable;

        // ASYNC SEARCH for repeatable fields (e.g., RFQ Workflow item search)
        if (field.async_search && field.options_url) {
          const repeatableInitialOptions = repeatableSelectOptions.map(opt => ({
            value: opt.value,
            label: opt.label,
            has_vendor: opt.has_vendor  // Include vendor config status for RFQ workflow
          }));

          const repeatableSelectedValue = fieldValue
            ? repeatableInitialOptions.find(opt => opt.value === fieldValue) || { value: fieldValue, label: rowData[`${field.name}_label`] || fieldValue }
            : null;

          // Async load function for searching
          const loadRepeatableAsyncOptions = async (inputValue) => {
            if (!inputValue || inputValue.length < 2) {
              return repeatableInitialOptions.slice(0, 50);
            }

            try {
              // Get token from prop first, then localStorage/sessionStorage
              const authToken = token || localStorage.getItem('token') || sessionStorage.getItem('token');
              if (!authToken) {
                console.warn('No auth token available for async search');
                return repeatableInitialOptions;
              }
              const separator = field.options_url.includes('?') ? '&' : '?';
              const url = `${field.options_url}${separator}search=${encodeURIComponent(inputValue)}&token=${authToken}`;

              const response = await fetch(url, {
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json'
                }
              });

              if (!response.ok) return repeatableInitialOptions;

              const data = await response.json();
              let dataArray = data.items || data.subsidiaries || data.vendors || data.customers || [];

              const labelField = field.options_label_field || 'name';
              const valueField = field.options_value_field || 'id';

              return dataArray.map(item => ({
                value: String(item[valueField]),
                label: item[labelField] || item.name || item.displayname || `Item ${item[valueField]}`,
                has_vendor: item.has_vendor  // Include vendor configuration status
              }));
            } catch (error) {
              console.error('Async search error:', error);
              return repeatableInitialOptions;
            }
          };

          return (
            <AsyncSelect
              inputId={fieldId}
              name={fieldId}
              defaultOptions={repeatableInitialOptions.slice(0, 50)}
              loadOptions={loadRepeatableAsyncOptions}
              value={repeatableSelectedValue}
              onChange={(selected) => {
                handleRepeatableItemChange(sectionName, rowIndex, field.name, selected?.value || '', selected?.label);
                if (selected?.label) {
                  handleRepeatableItemChange(sectionName, rowIndex, `${field.name}_label`, selected.label);
                }
                // Check if item has vendors configured (ONLY for RFQ workflow)
                if (isRFQWorkflow()) {
                  if (selected?.value && selected?.has_vendor === false) {
                    console.log('[VENDOR CHECK] Item has no vendors configured:', selected.value, selected.label);
                    setVendorWarnings(prev => ({
                      ...prev,
                      [selected.value]: {
                        itemId: selected.value,
                        itemLabel: selected.label
                      }
                    }));
                  } else if (selected?.value) {
                    // Remove warning if item has vendors
                    setVendorWarnings(prev => {
                      const updated = { ...prev };
                      delete updated[selected.value];
                      return updated;
                    });
                  }
                }
              }}
              cacheOptions
              isClearable
              isSearchable
              placeholder={field.placeholder || 'Type to search...'}
              noOptionsMessage={({ inputValue }) => inputValue?.length < 2 ? 'Type 2+ characters to search' : 'No results found'}
              loadingMessage={() => 'Searching...'}
              styles={{
                ...(isDarkMode ? selectStylesDark : selectStyles),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          );
        }

        if (useRepeatableSearchable) {
          // Convert options to react-select format
          const repeatableReactOptions = repeatableSelectOptions.map(opt => ({
            value: opt.value,
            label: opt.label
          }));

          console.log(`🎯 Repeatable Select render: field=${field.name}, reactOptions=${repeatableReactOptions.length}, first option:`, repeatableReactOptions[0]);

          const repeatableSelectedValue = fieldValue
            ? repeatableReactOptions.find(opt => opt.value === fieldValue)
            : null;

          return (
            <Select
              inputId={fieldId}
              name={fieldId}
              options={repeatableReactOptions}
              value={repeatableSelectedValue}
              onChange={(selected) => {
                handleRepeatableItemChange(sectionName, rowIndex, field.name, selected?.value || '', selected?.label);
                // Also store the label
                if (selected?.label) {
                  handleRepeatableItemChange(sectionName, rowIndex, `${field.name}_label`, selected.label);
                }
              }}
              isLoading={isRepeatableLoading}
              isDisabled={isRepeatableLoading}
              isClearable
              isSearchable
              placeholder={isRepeatableLoading ? 'Loading...' : (field.placeholder || `Search ${field.label}...`)}
              noOptionsMessage={() => isRepeatableLoading ? 'Loading...' : 'No options found'}
              styles={{
                ...(isDarkMode ? selectStylesDark : selectStyles),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          );
        }

        // Standard HTML select for non-searchable fields
        return (
          <div className="relative">
            <select {...commonProps} disabled={isRepeatableLoading}>
              <option value="">
                {isRepeatableLoading ? 'Loading...' : (field.placeholder || `Select ${field.label}`)}
              </option>
              {repeatableSelectOptions.map((option, idx) => (
                <option key={idx} value={option.value}>{option.label}</option>
              ))}
            </select>
            {isRepeatableLoading && (
              <div className="absolute right-8 top-1/2 -translate-y-1/2">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
              </div>
            )}
          </div>
        );
      case 'number':
        return (
          <input
            type="number"
            {...commonProps}
            min={field.min}
            max={field.max}
            step={field.step || 1}
          />
        );
      case 'date':
        // Support min_field for dynamic date validation in repeatable fields
        // Check row data first, then parent formData
        const repeatableMinDate = field.min_field
          ? (rowData[field.min_field] || formData[field.min_field])
          : field.min;
        const repeatableMaxDate = field.max_field
          ? (rowData[field.max_field] || formData[field.max_field])
          : field.max;
        return (
          <input
            type="date"
            {...commonProps}
            min={repeatableMinDate}
            max={repeatableMaxDate}
          />
        );
      default:
        return <input type="text" {...commonProps} />;
    }
  };

  const renderSection = (section, sectionIndex = 0) => {
    if (section.type === 'field_group') {
      // Handle repeatable sections (like line items)
      if (section.repeatable && section.name) {
        const items = formData[section.name] || [{}];

        return (
          <div key={`section-${sectionIndex}`} className={section.css_class || 'mb-6'}>
            {section.title && (
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {section.title}
              </h3>
            )}

            {/* Header row with field labels */}
            <div className="hidden md:grid md:gap-3 mb-2" style={{ gridTemplateColumns: `repeat(${section.fields?.length || 1}, 1fr) 40px` }}>
              {section.fields?.map((field) => (
                <div key={`header-${field.name}`} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </div>
              ))}
              <div></div> {/* Placeholder for delete button column */}
            </div>

            {/* Repeatable rows */}
            {items.map((rowData, rowIndex) => (
              <div
                key={`row-${rowIndex}`}
                className="flex flex-col md:grid md:gap-3 mb-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                style={{ gridTemplateColumns: `repeat(${section.fields?.length || 1}, 1fr) 40px` }}
              >
                {section.fields?.map((field) => (
                  <div key={`${rowIndex}-${field.name}`} className="mb-2 md:mb-0">
                    {/* Mobile label */}
                    <label className="md:hidden block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderRepeatableField(field, section.name, rowIndex, rowData)}
                  </div>
                ))}

                {/* Delete button */}
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => removeRepeatableRow(section.name, rowIndex)}
                    disabled={items.length <= 1}
                    className={`p-2 rounded-md transition-colors ${
                      items.length <= 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30'
                    }`}
                    title="Remove row"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>

                {/* EOL Warning Banner */}
                {(() => {
                  const itemId = rowData?.netsuite_item_id || rowData?.item_id;
                  const eolInfo = itemId ? eolWarnings[itemId] : null;
                  if (eolInfo) {
                    return (
                      <div className="col-span-full mt-2 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
                        <div className="flex items-start gap-2">
                          <span className="text-amber-600 dark:text-amber-400 text-lg">⚠️</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                              End of Life Item
                            </p>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                              <strong>{eolInfo.itemLabel || eolInfo.eol_item_name}</strong> is discontinued/EOL.
                              {eolInfo.reason && <span> Reason: {eolInfo.reason}</span>}
                            </p>
                            {eolInfo.replacement_item_name && (
                              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                                <strong>Suggested Replacement:</strong> {eolInfo.replacement_item_name}
                                {eolInfo.replacement_netsuite_item_id && (
                                  <span className="text-xs ml-1">(ID: {eolInfo.replacement_netsuite_item_id})</span>
                                )}
                              </p>
                            )}
                            {eolInfo.source_vendor_name && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                Reported by: {eolInfo.source_vendor_name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* No Vendor Configured Warning Banner - Only for RFQ Workflow */}
                {isRFQWorkflow() && (() => {
                  const itemId = rowData?.netsuite_item_id || rowData?.item_id;
                  const vendorInfo = itemId ? vendorWarnings[itemId] : null;
                  if (vendorInfo) {
                    return (
                      <div className="col-span-full mt-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                        <div className="flex items-start gap-2">
                          <span className="text-red-600 dark:text-red-400 text-lg">🚫</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-red-800 dark:text-red-200">
                              No Vendor Configured
                            </p>
                            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                              <strong>{vendorInfo.itemLabel}</strong> does not have any vendors configured in NetSuite.
                            </p>
                            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                              Please configure vendors for this item in NetSuite before creating an RFQ.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}

            {/* Add row button */}
            <button
              type="button"
              onClick={() => addRepeatableRow(section.name)}
              className="mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add {section.title?.replace(/s$/, '') || 'Item'}
            </button>
          </div>
        );
      }

      // Regular (non-repeatable) field group
      const isGridLayout = section.layout === 'grid';
      const columns = section.columns || 2;

      return (
        <div key={`section-${sectionIndex}`} className={section.css_class || 'mb-6'}>
          {section.title && (
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {section.title}
            </h3>
          )}

          <div
            className={isGridLayout ? 'grid gap-4' : ''}
            style={isGridLayout ? { gridTemplateColumns: `repeat(${columns}, 1fr)` } : {}}
          >
            {section.fields?.map((field) => (
              <div key={`field-${field.name}`} className={field.css_class || (isGridLayout ? '' : 'mb-4')}>
                {/* Only show label for non-file fields */}
                {field.type !== 'file' && (
                  <label
                    htmlFor={field.name}
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                )}

                {field.type === 'checkbox' ? (
                  <div className="flex items-center">
                    {renderField(field)}
                    <label
                      htmlFor={field.name}
                      className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                    >
                      {field.label}
                    </label>
                  </div>
                ) : (
                  renderField(field)
                )}

                {errors[field.name] && (
                  <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div key={`section-${sectionIndex}`} className={section.css_class || ''}>
        {section.content?.map((content, index) => (
          <div key={`content-${index}`}>
            {renderContent(content, index)}
          </div>
        ))}
      </div>
    );
  };

  const parseStyle = (styleStr) => {
    if (!styleStr) return {};
    const styles = {};
    styleStr.split(';').forEach(rule => {
      const [property, value] = rule.split(':').map(s => s.trim());
      if (property && value) {
        const camelCaseProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        styles[camelCaseProperty] = value;
      }
    });
    return styles;
  };

  const parseMaxSize = (sizeStr) => {
    if (!sizeStr) return Infinity;
    const unit = sizeStr.toLowerCase().slice(-2);
    const value = parseInt(sizeStr);

    switch (unit) {
      case 'kb': return value * 1024;
      case 'mb': return value * 1024 * 1024;
      case 'gb': return value * 1024 * 1024 * 1024;
      default: return value;
    }
  };

  // Form content (shared between embedded and modal modes)
  const formContent = (
    <div className={isEmbedded ? '' : 'relative'}>
      {/* Close button - only show in standalone modal mode */}
      {!isEmbedded && (
        <div className="absolute top-4 right-4 z-10">
          <button
            onClick={onClose}
            data-close-form="true"
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>
      )}

      {/* Header */}
      {definition.header && (
        <div className={definition.header.css_class || ''}>
          {definition.header.sections?.map((section, index) => (
            <div key={`header-${index}`}>
              {renderContent(section, index)}
            </div>
          ))}
        </div>
      )}

      {/* Form Sections */}
      <div className={isEmbedded ? '' : 'p-6'}>
        {definition.sections?.map((section, index) => (
          <div key={`form-section-${index}`}>
            {renderSection(section, index)}
          </div>
        ))}

        {/* Submit and Cancel Buttons for Workflow Forms */}
        {definition.actions?.submit && (
          <div className="flex items-center gap-3 mt-6">
            <button
              onClick={() => handleAction('submit', definition.actions.submit)}
              disabled={externalLoading || loading}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: '600',
                padding: '0.875rem 2rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: externalLoading || loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '1rem',
                boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
                opacity: externalLoading || loading ? 0.6 : 1,
                flex: '1'
              }}
            >
              {(externalLoading || loading) && (
                <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
              )}
              {(externalLoading || loading)
                ? (definition.actions.submit.loading_message || 'Processing...')
                : (definition.submit_text || definition.actions.submit.button_text || 'Submit')
              }
            </button>

            {/* Cancel button - closes form via onClose callback */}
            <button
              type="button"
              onClick={() => {
                console.log('🚫 Cancel button clicked - calling onClose');
                if (onClose) {
                  onClose();
                }
              }}
              disabled={externalLoading || loading}
              style={{
                background: '#6b7280',
                color: 'white',
                fontWeight: '600',
                padding: '0.875rem 2rem',
                borderRadius: '0.5rem',
                border: 'none',
                cursor: externalLoading || loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                fontSize: '1rem',
                opacity: externalLoading || loading ? 0.5 : 1,
                flex: '1'
              }}
              onMouseEnter={(e) => !externalLoading && !loading && (e.target.style.background = '#4b5563')}
              onMouseLeave={(e) => !externalLoading && !loading && (e.target.style.background = '#6b7280')}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      {definition.footer && (
        <div className={definition.footer.css_class || ''}>
          {definition.footer.content?.map((content, index) => (
            <div key={`footer-${index}`}>
              {renderContent(content, index)}
            </div>
          ))}
        </div>
      )}

      {/* Bank Workflow Only: Processing Overlay with Progress Messages */}
      {externalLoading && isBankWorkflow && (
        <div
          className="absolute inset-0 bg-white bg-opacity-95 dark:bg-gray-900 dark:bg-opacity-95 flex flex-col items-center justify-center rounded-lg z-50"
          style={{ backdropFilter: 'blur(4px)' }}
        >
          <div className="flex flex-col items-center space-y-6 p-8">
            {/* Spinning Logo */}
            {/* Old SpinningSankalvaXLogo - commented out
            <SpinningSankalvaXLogo
              size="w-20 h-20"
              className="text-purple-600 dark:text-purple-400"
              showBackground={true}
            />
            */}
            {/* New SpinningGruveLogo */}
            <SpinningGruveLogo
              size="w-20 h-20"
              className="text-purple-600 dark:text-purple-400"
              showBackground={true}
            />

            {/* Progress Message */}
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">
                Processing Your Bank Statement
              </h3>
              <p className="text-lg text-purple-600 dark:text-purple-400 font-medium animate-pulse">
                {bankProcessingMessages[processingMessageIndex]}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                This may take a few moments. Please don't close this window.
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="w-64 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-600 to-pink-600 rounded-full animate-pulse"
                style={{
                  width: `${((processingMessageIndex + 1) / bankProcessingMessages.length) * 100}%`,
                  transition: 'width 3s ease-in-out'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Handler for when scanner captures a file
  const handleScanCapture = (scannedFile) => {
    console.log('📷 Scanned file received:', scannedFile);
    if (scannerFieldName && scannedFile) {
      handleInputChange(scannerFieldName, scannedFile);
    }
    setShowScanner(false);
    setScannerFieldName(null);
  };

  // Document Scanner Modal (rendered separately, works in both modes)
  const scannerModal = showScanner && (
    <DocumentScanner
      onCapture={handleScanCapture}
      onClose={() => {
        setShowScanner(false);
        setScannerFieldName(null);
      }}
    />
  );

  // When embedded (inside another modal like UniversalStepManager), render without modal wrapper
  if (isEmbedded) {
    return (
      <>
        {formContent}
        {scannerModal}
      </>
    );
  }

  // Standalone mode - render with modal wrapper
  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto relative"
          style={{
            width: definition.config?.width || '600px',
            ...(definition.config?.custom_css ? parseStyle(definition.config.custom_css) : {})
          }}
        >
          {formContent}
        </div>
      </div>
      {scannerModal}
    </>
  );
};

export default FormRenderer;